import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api from "@/lib/api";
import PublicHeader from "@/components/layout/PublicHeader";
import Footer from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Mic, Users, Handshake, Megaphone, Send, CheckCircle2, Loader2, Sparkles } from "lucide-react";

const WHY = [
  { icon: Users, t: "Reach an engaged room", d: "Connect with local business owners and community leaders, every single Friday." },
  { icon: Mic, t: "Built-in content", d: "Be featured on our live podcast and walk away with audio, video, transcript & clips." },
  { icon: Handshake, t: "Community goodwill", d: "Back real conversations and deeper connection across Spokane." },
];

const INQUIRY_TYPES = [
  { value: "sponsor", label: "Sponsor a Friday" },
  { value: "guest", label: "Be a guest" },
  { value: "both", label: "Both" },
  { value: "general", label: "General inquiry" },
];

const FRIDAYS = [
  { value: "1", label: "1st Friday \u00b7 Arts & Entertainment" },
  { value: "2", label: "2nd Friday \u00b7 Generosity" },
  { value: "3", label: "3rd Friday \u00b7 Youth & Next Generation" },
  { value: "4", label: "4th Friday \u00b7 Intentionality" },
];

const EMPTY = { name: "", email: "", company: "", phone: "", inquiry_type: "sponsor", preferred_friday: "", message: "" };

export default function Partner() {
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please add your name, email, and a short message.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/inquiries", {
        ...form,
        preferred_friday: form.preferred_friday ? parseInt(form.preferred_friday, 10) : null,
      });
      setDone(true);
    } catch (err) {
      toast.error("Could not send your inquiry. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <PublicHeader />

      <div className="fc-hero-bg fc-paper-noise">
        <div className="mx-auto w-full max-w-6xl px-4 pb-10 pt-14 sm:px-6 lg:px-8">
          <span className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1 text-xs font-medium text-muted-foreground">
            <Megaphone className="h-3.5 w-3.5 text-primary" /> Partner with us
          </span>
          <h1 className="mt-4 max-w-3xl font-serif text-3xl font-semibold tracking-[-0.02em] text-foreground sm:text-4xl lg:text-5xl">
            Sponsor a Friday or join us as a guest
          </h1>
          <p className="mt-4 max-w-[62ch] text-base leading-7 text-muted-foreground sm:text-lg">
            Friday Conversations brings together business owners and community members every week in
            Spokane. Partner with us to reach the room, get featured on the podcast, and support real
            conversations and deeper connection.
          </p>
        </div>
      </div>

      <div className="mx-auto grid w-full max-w-6xl gap-10 px-4 py-12 sm:px-6 lg:grid-cols-2 lg:px-8">
        {/* Why partner */}
        <div>
          <h2 className="font-serif text-2xl font-semibold text-foreground">Why partner</h2>
          <div className="mt-5 space-y-4">
            {WHY.map((w) => (
              <Card key={w.t} className="border-border bg-card" data-testid={`partner-why-${w.t.split(" ")[0].toLowerCase()}`}>
                <CardContent className="flex items-start gap-4 p-5">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-border bg-secondary text-primary">
                    <w.icon className="h-5 w-5" />
                  </span>
                  <div>
                    <h3 className="text-base font-semibold text-foreground">{w.t}</h3>
                    <p className="mt-1 text-sm text-muted-foreground">{w.d}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          <div className="mt-5 flex items-center gap-2 rounded-xl border border-border bg-secondary/50 p-4 text-sm text-muted-foreground">
            <Sparkles className="h-4 w-4 shrink-0 text-primary" />
            Prefer email? Reach us directly at{" "}
            <a href="mailto:Chris@wannaware.com" className="font-medium text-primary hover:underline" data-testid="partner-email-link">Chris@wannaware.com</a>
          </div>
        </div>

        {/* Inquiry form / success */}
        <div>
          {done ? (
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}>
              <Card className="border-border bg-card" data-testid="partner-success">
                <CardContent className="p-8 text-center">
                  <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-secondary text-primary">
                    <CheckCircle2 className="h-7 w-7" />
                  </div>
                  <h3 className="mt-4 font-serif text-xl font-semibold text-foreground">Thank you — we got it!</h3>
                  <p className="mx-auto mt-2 max-w-sm text-sm text-muted-foreground">
                    Our team will be in touch soon. In the meantime, feel free to explore the Fridays
                    and the podcast.
                  </p>
                  <div className="mt-6 flex justify-center gap-3">
                    <Button asChild variant="secondary"><Link to="/">Back home</Link></Button>
                    <Button asChild className="bg-primary text-primary-foreground hover:bg-[hsl(var(--primary-hover))]"><Link to="/#rhythm">See the Fridays</Link></Button>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ) : (
            <Card className="border-border bg-card">
              <CardContent className="p-6 sm:p-7">
                <h2 className="font-serif text-2xl font-semibold text-foreground">Tell us about it</h2>
                <p className="mt-1 text-sm text-muted-foreground">We'll reply by email. Required fields are marked.</p>
                <form className="mt-5 space-y-4" onSubmit={submit} data-testid="partner-form">
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div><Label htmlFor="p-name">Name *</Label><Input id="p-name" className="mt-1.5" value={form.name} onChange={set("name")} data-testid="partner-name-input" /></div>
                    <div><Label htmlFor="p-email">Email *</Label><Input id="p-email" type="email" className="mt-1.5" value={form.email} onChange={set("email")} data-testid="partner-email-input" /></div>
                    <div><Label htmlFor="p-company">Company / Organization</Label><Input id="p-company" className="mt-1.5" value={form.company} onChange={set("company")} data-testid="partner-company-input" /></div>
                    <div><Label htmlFor="p-phone">Phone</Label><Input id="p-phone" className="mt-1.5" value={form.phone} onChange={set("phone")} data-testid="partner-phone-input" /></div>
                  </div>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div>
                      <Label>I'm interested in</Label>
                      <Select value={form.inquiry_type} onValueChange={(v) => setForm({ ...form, inquiry_type: v })}>
                        <SelectTrigger className="mt-1.5" data-testid="partner-type-select"><SelectValue /></SelectTrigger>
                        <SelectContent>{INQUIRY_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Preferred Friday <span className="text-xs text-muted-foreground">(optional)</span></Label>
                      <Select value={form.preferred_friday || "any"} onValueChange={(v) => setForm({ ...form, preferred_friday: v === "any" ? "" : v })}>
                        <SelectTrigger className="mt-1.5" data-testid="partner-friday-select"><SelectValue placeholder="No preference" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="any">No preference</SelectItem>
                          {FRIDAYS.map((f) => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div><Label htmlFor="p-message">Message *</Label><Textarea id="p-message" rows={5} className="mt-1.5" value={form.message} onChange={set("message")} placeholder="Tell us a little about what you have in mind." data-testid="partner-message-input" /></div>
                  <Button type="submit" disabled={submitting} className="w-full bg-primary text-primary-foreground hover:bg-[hsl(var(--primary-hover))]" data-testid="partner-submit-button">
                    {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="mr-1.5 h-4 w-4" />Send inquiry</>}
                  </Button>
                </form>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
}
